import React from 'react';
import styled from 'styled-components';
import SEO from '../components/SEO';

const NavbarSpacer = styled.div`
  height: 120px;
  @media (min-width: 768px) {
    height: 140px;
  }
  @media (min-width: 1024px) {
    height: 160px;
  }
`;

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #fff;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 16px;
  
  @media (min-width: 768px) {
    padding: 60px 24px;
  }
  
  @media (min-width: 1024px) {
    padding: 80px 24px;
  }
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 20px;
  
  @media (min-width: 768px) {
    font-size: 48px;
  }
`;

const LastUpdated = styled.p`
  color: #666;
  font-size: 14px;
  margin-bottom: 40px;
`;

const Section = styled.section`
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
  margin-top: 32px;
  
  @media (min-width: 768px) {
    font-size: 28px;
  }
`;

const Paragraph = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  margin-bottom: 16px;
`;

const List = styled.ul`
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  margin-bottom: 16px;
  padding-left: 24px;
`;

const ListItem = styled.li`
  margin-bottom: 8px;
`;

const HighlightBox = styled.div`
  background: #f0f9ff;
  border-left: 4px solid #0ea5e9;
  padding: 20px;
  margin: 24px 0;
  border-radius: 4px;
`;

const HighlightTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #0369a1;
`;

const HighlightText = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: #0369a1;
  margin-bottom: 8px;
`;

const ContactInfo = styled.div`
  background: #f5f5f5;
  padding: 24px;
  border-radius: 8px;
  margin-top: 32px;
`;

const ContactText = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  margin-bottom: 8px;
`;

const ShippingPolicyPage = () => {
  return (
    <PageContainer>
      <SEO 
        title="Shipping Policy"
        description="TheMadZone shipping policy. Free shipping on orders over £100. UK delivery only. Standard 3-5 business days delivery. Track your order."
        keywords="shipping policy, delivery, UK shipping, free shipping, TheMadZone delivery"
      />
      <NavbarSpacer />
      <ContentWrapper>
        <Title>Shipping Policy</Title>
        <LastUpdated>Last Updated: {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}</LastUpdated>

        <Section>
          <Paragraph>
            At The Mad Zone, we are committed to delivering your orders in a timely, reliable, and efficient manner. This Shipping Policy outlines our delivery locations, shipping costs, processing times, and other important information to help ensure a smooth shopping experience.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>1. Shipping Areas</SectionTitle>
          
          <HighlightBox>
            <HighlightTitle>United Kingdom Only</HighlightTitle>
            <HighlightText>
              At this time, The Mad Zone ships exclusively within the United Kingdom. We do not currently offer international shipping.
            </HighlightText>
          </HighlightBox>
          
          <Paragraph>
            We deliver to the following locations:
          </Paragraph>
          <List>
            <ListItem>England</ListItem>
            <ListItem>Wales</ListItem>
            <ListItem>Scotland</ListItem>
            <ListItem>Northern Ireland</ListItem>
            <ListItem>Isle of Man</ListItem>
            <ListItem>Isle of Skye</ListItem>
          </List>
          
          <Paragraph>
            Unfortunately, we are unable to fulfill orders for delivery outside these areas. We appreciate your understanding and hope to expand our shipping coverage in the future.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>2. Shipping Costs</SectionTitle>
          
          <List>
            <ListItem><strong>Orders of £100.00 or more:</strong> Free standard delivery</ListItem>
            <ListItem><strong>Orders under £100.00:</strong> £4.00 flat-rate delivery fee</ListItem>
          </List>
          
          <HighlightBox>
            <HighlightTitle>Important Information:</HighlightTitle>
            <HighlightText>
              The £4.00 delivery charge for orders under £100.00 is fixed and non-refundable, even if items are returned. This fee covers order handling, processing, and shipping costs.
            </HighlightText>
            <HighlightText>
              Shipping charges are calculated based on the order subtotal before any discounts or promotional codes are applied. All applicable delivery charges will be clearly displayed at checkout prior to payment.
            </HighlightText>
          </HighlightBox>
        </Section>

        <Section>
          <SectionTitle>3. Delivery Times</SectionTitle>
          
          <Paragraph>
            We aim to process and dispatch all orders within 1–2 business days. Once dispatched, estimated delivery times are as follows:
          </Paragraph>
          <List>
            <ListItem><strong>Standard Delivery:</strong> 3–5 business days</ListItem>
            <ListItem><strong>Express Delivery:</strong> 1–2 business days (where available)</ListItem>
          </List>
          
          <Paragraph>
            Business days are Monday to Friday, excluding bank holidays and public holidays.
          </Paragraph>
          
          <Paragraph>
            Please note that delivery timeframes are estimates and may be affected by factors beyond our control, including adverse weather conditions, courier delays, or increased demand during peak periods.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>4. Order Processing</SectionTitle>
          
          <Paragraph>
            After placing an order, you will receive an order confirmation email. Once your order has been dispatched, a shipping confirmation email containing tracking details will be sent to you.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>5. Order Tracking</SectionTitle>
          
          <Paragraph>
            When your order is shipped, a tracking number will be provided via email. You may use this number to track your delivery through the courier's website. Order status updates are also available by logging into your account and visiting the "My Orders" section.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>6. Delivery Address Accuracy</SectionTitle>
          
          <Paragraph>
            Please ensure that all delivery details are complete and accurate at checkout. The Mad Zone cannot be held responsible for deliveries made to incorrect or incomplete addresses provided by the customer.
          </Paragraph>
          
          <Paragraph>
            If you need to amend your delivery address after placing an order, please contact us as soon as possible at <strong>hello@themadzone.com</strong>. If an order is returned due to an incorrect address, additional shipping fees may apply to resend the order.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>7. Failed Delivery Attempts</SectionTitle>
          
          <Paragraph>
            If delivery is unsuccessful (for example, if no one is available to receive the parcel), the courier may leave instructions for rearranging delivery or collection. If the parcel is not collected or redelivery is not arranged within the courier's specified timeframe, it may be returned to us.
          </Paragraph>
          
          <Paragraph>
            In such cases, customers will be responsible for any additional shipping costs required to resend the order.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>8. Damaged or Lost Packages</SectionTitle>
          
          <Paragraph>
            If your order arrives damaged or is lost in transit, please contact us promptly at <strong>hello@themadzone.com</strong>, including your order number and a description of the issue. We will investigate the matter with the courier and arrange a replacement or reshipment at no additional cost where applicable.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>9. Multiple Item Orders</SectionTitle>
          
          <Paragraph>
            Orders containing multiple items may be dispatched in separate shipments if items are available at different times. There will be no additional delivery charges for split shipments. Separate tracking details will be provided for each dispatch.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>10. International Shipping</SectionTitle>
          
          <Paragraph>
            The Mad Zone does not offer international shipping. Delivery is available only within the United Kingdom, including England, Wales, Scotland, Northern Ireland, the Isle of Man, and the Isle of Skye.
          </Paragraph>
          
          <Paragraph>
            We apologize for any inconvenience this may cause and appreciate your understanding.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle style={{marginTop: '60px'}}>Return Policy</SectionTitle>
          
          <Paragraph>
            At The Mad Zone, customer satisfaction is of utmost importance to us. If your purchase does not meet your expectations, we are pleased to offer a return option in accordance with the policy outlined below.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>1. Return Timeframe</SectionTitle>
          
          <Paragraph>
            Items may be returned within 7 days of the delivery date, as confirmed by the shipping carrier.
          </Paragraph>
          
          <HighlightBox>
            <HighlightTitle>Important Notice:</HighlightTitle>
            <HighlightText>
              Returns must be initiated and dispatched within this 7-day period. Any returns received outside of this timeframe will regrettably not be accepted.
            </HighlightText>
          </HighlightBox>
        </Section>

        <Section>
          <SectionTitle>2. Return Eligibility & Conditions</SectionTitle>
          
          <Paragraph>
            To qualify for a return, all items must:
          </Paragraph>
          <List>
            <ListItem>Be unworn, unwashed, and in their original condition</ListItem>
            <ListItem>Have all original tags and labels securely attached</ListItem>
            <ListItem>Be returned in the original packaging (where applicable)</ListItem>
            <ListItem>Include all accessories, promotional items, or complimentary products received with the order</ListItem>
          </List>
          
          <Paragraph>
            Items that show signs of wear, damage, or are missing original tags may be declined or subject to a reduced refund at our discretion.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>3. Return Procedure</SectionTitle>
          
          <Paragraph>
            To ensure prompt and accurate processing, please follow the steps below:
          </Paragraph>
          <List>
            <ListItem>Contact us at hello@themadzone.com, quoting your order number and reason for return</ListItem>
            <ListItem>Carefully package the item(s), preferably in the original packaging</ListItem>
            <ListItem>Include a note inside the parcel clearly stating your Order ID</ListItem>
            <ListItem>Send the return using a tracked postal service to the address provided below</ListItem>
            <ListItem>Retain your shipping receipt as proof of return</ListItem>
          </List>
          
          <HighlightBox>
            <HighlightTitle>Required Information:</HighlightTitle>
            <HighlightText>
              Returns must include the Order ID. Returns received without this information may experience processing delays or may be refused.
            </HighlightText>
          </HighlightBox>
        </Section>

        <Section>
          <SectionTitle>4. Return Address</SectionTitle>
          
          <Paragraph>
            Please send all returns to:
          </Paragraph>
          <ContactInfo>
            <ContactText>
              The Mad Zone – Returns<br />
              Apartment 2, 45–47 Savile Court<br />
              Hull, England<br />
              HU1 3BT<br />
              United Kingdom
            </ContactText>
          </ContactInfo>
          
          <Paragraph style={{marginTop: '20px'}}>
            We strongly recommend using a tracked delivery service. The Mad Zone cannot be held responsible for returns lost in transit.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>5. Return Shipping Costs</SectionTitle>
          
          <Paragraph>
            Customers are responsible for all return shipping costs. These charges are non-refundable unless the item is confirmed to be faulty or incorrect.
          </Paragraph>
          
          <Paragraph>
            For your protection, we advise using a tracked shipping service, as we cannot process returns that fail to reach us.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>6. Refund Processing</SectionTitle>
          
          <Paragraph>
            Upon receipt of your return:
          </Paragraph>
          <List>
            <ListItem>Items will be inspected within 5–7 business days</ListItem>
            <ListItem>Once approved, a refund will be issued to the original payment method</ListItem>
            <ListItem>Please allow 5–10 business days for the refund to appear, depending on your payment provider</ListItem>
          </List>
          
          <Paragraph>
            You will receive an email confirmation once your refund has been processed.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>7. Exchanges</SectionTitle>
          
          <Paragraph>
            At this time, we do not offer direct exchanges.
          </Paragraph>
          
          <Paragraph>
            If you wish to exchange an item, please return the original purchase in accordance with this policy and place a new order for the desired item. Once your return has been approved, a refund will be issued for the original item.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>8. Non-Returnable Items</SectionTitle>
          
          <Paragraph>
            The following items are not eligible for return:
          </Paragraph>
          <List>
            <ListItem>Items that have been worn, washed, or damaged</ListItem>
            <ListItem>Items returned without original tags or labels</ListItem>
            <ListItem>Items returned outside the 7-day return window</ListItem>
            <ListItem>Custom-made or personalized items</ListItem>
            <ListItem>Gift cards, which are non-refundable once purchased</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>9. Faulty or Incorrect Items</SectionTitle>
          
          <Paragraph>
            If you receive an item that is faulty or incorrect, please contact us immediately at <strong>hello@themadzone.com</strong>. Upon verification, we will arrange a complimentary return and issue a full refund, including any applicable return shipping costs.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>10. Refund Amount</SectionTitle>
          
          <Paragraph>
            Refunds are issued for the purchase price of the returned item(s) only. Original shipping charges are non-refundable unless the return is due to a fault or error on our part.
          </Paragraph>
          
          <Paragraph>
            Any discounts or promotional offers applied at the time of purchase will be reflected in the refunded amount.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>11. Right to Refuse Returns</SectionTitle>
          
          <Paragraph>
            The Mad Zone reserves the right to refuse any return that does not comply with the terms outlined in this policy. Items returned in violation of these conditions may be sent back to the customer at their expense.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle style={{marginTop: '60px'}}>Privacy Policy</SectionTitle>
          
          <Paragraph>
            At The Mad Zone, we place the highest value on the privacy, discretion, and trust of our clientele. This Privacy Policy explains, with clarity and transparency, how we collect, use, protect, and — where necessary — share your personal information when you interact with our website and services.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>1. Information We Collect</SectionTitle>
          
          <Paragraph>
            To deliver a seamless and refined experience, we may collect the following information:
          </Paragraph>

          <HighlightBox>
            <HighlightTitle>Personal Information</HighlightTitle>
            <HighlightText>
              When you create an account, place an order, or communicate with us, we may collect:
            </HighlightText>
          </HighlightBox>

          <List>
            <ListItem>Full name and contact details (email address and telephone number)</ListItem>
            <ListItem>Billing and delivery addresses</ListItem>
            <ListItem>Payment information (processed exclusively via secure, trusted payment providers)</ListItem>
            <ListItem>Date of birth, where required for age verification</ListItem>
            <ListItem>Encrypted account login credentials</ListItem>
          </List>

          <HighlightBox style={{marginTop: '20px'}}>
            <HighlightTitle>Information Collected Automatically</HighlightTitle>
            <HighlightText>
              When you visit our website, we may automatically collect:
            </HighlightText>
          </HighlightBox>

          <List>
            <ListItem>IP address and device details</ListItem>
            <ListItem>Browser type and version</ListItem>
            <ListItem>Pages viewed and time spent on the site</ListItem>
            <ListItem>Referring websites or links</ListItem>
            <ListItem>Cookies and similar tracking technologies</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>2. How We Use Your Information</SectionTitle>
          
          <Paragraph>
            Your information is used thoughtfully and purposefully to:
          </Paragraph>
          <List>
            <ListItem>Process and fulfil orders efficiently</ListItem>
            <ListItem>Communicate regarding purchases, accounts, and service enquiries</ListItem>
            <ListItem>Provide curated marketing communications where consent has been given</ListItem>
            <ListItem>Improve our website, services, and overall customer experience</ListItem>
            <ListItem>Detect and prevent fraud and maintain platform security</ListItem>
            <ListItem>Comply with legal and regulatory obligations</ListItem>
            <ListItem>Manage referral, loyalty, and promotional programmes</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>3. Information Sharing & Disclosure</SectionTitle>
          
          <Paragraph>
            <strong>We never sell your personal information.</strong>
          </Paragraph>
          
          <Paragraph>
            Your data may be shared only with:
          </Paragraph>
          <List>
            <ListItem>Trusted service partners such as payment processors, delivery services, and email providers</ListItem>
            <ListItem>Legal or regulatory authorities where required by law</ListItem>
            <ListItem>Business successors in the event of a merger, acquisition, or asset transfer</ListItem>
          </List>
          
          <Paragraph>
            All third parties are contractually bound to safeguard your data and use it solely for authorised purposes.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>4. Data Security</SectionTitle>
          
          <Paragraph>
            We employ advanced technical and organisational measures to protect your personal information against unauthorised access, loss, or misuse. While we maintain rigorous security standards, no digital system can be guaranteed to be entirely secure.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>5. Cookies & Tracking Technologies</SectionTitle>
          
          <Paragraph>
            We use cookies and similar technologies to enhance site functionality, analyse engagement, and personalise content. You may manage or disable cookies through your browser settings, though this may affect certain features of the website.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>6. Your Rights</SectionTitle>
          
          <Paragraph>
            In accordance with UK data protection laws, you have the right to:
          </Paragraph>
          <List>
            <ListItem>Access your personal data</ListItem>
            <ListItem>Request correction of inaccurate or incomplete information</ListItem>
            <ListItem>Request deletion of your data</ListItem>
            <ListItem>Object to or restrict certain processing activities</ListItem>
            <ListItem>Request data portability</ListItem>
            <ListItem>Withdraw consent where processing is consent-based</ListItem>
          </List>
          
          <Paragraph>
            To exercise any of these rights, please contact us at <strong>hello@themadzone.com</strong>.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>7. Data Retention</SectionTitle>
          
          <Paragraph>
            We retain personal information only for as long as necessary to fulfil the purposes outlined in this policy or to comply with legal requirements. Once no longer required, data is securely deleted or anonymised.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>8. Children's Privacy</SectionTitle>
          
          <Paragraph>
            Our services are intended exclusively for individuals aged 18 and over. We do not knowingly collect personal information from minors. If you believe a child's data has been provided to us, please notify us immediately.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>9. International Data Transfers</SectionTitle>
          
          <Paragraph>
            Where necessary, your information may be transferred and processed outside the UK or EEA. In such cases, appropriate safeguards are implemented to ensure your data remains protected in line with applicable data protection standards.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>10. Updates to This Policy</SectionTitle>
          
          <Paragraph>
            We may update this Privacy Policy periodically to reflect changes in our practices or legal obligations. Any revisions will be published on this page along with the date of the latest update. We encourage you to review it from time to time.
          </Paragraph>
        </Section>


      </ContentWrapper>
    </PageContainer>
  );
};

export default ShippingPolicyPage;

